import { create } from "zustand";
import type { NbSignal, SclNode } from "../lib/jsonContracts";
import { loadModels, predictBatch, validateInScl } from "../wasm/client";
import { parseSclNodes, parseSignalList } from "../lib/referenceParsers";
import { usePagesStore } from "./pagesStore";

type Status = "idle"|"loading"|"ready"|"error";

// Estado individual de cada pestaña
export interface TabState {
  rows:        NbSignal[];
  sclNodes:    SclNode[];
  sclText?:    string;      // texto crudo del SCL de validación
  sclFileName?: string;
  mapSclText?: string;      // SCL de mapeo (independiente del de validación)
  mapSclFileName?: string;
  mapIeds?:    { name: string; lDevices: string[] }[];
  mapDatasets?: string[];
  mapIedTarget?: string;    // IED destino elegido para toda la pestaña
  hintMapButton?: boolean;  // dispara la flecha temporal hacia "Mapear nodos a SCL"
  modelOverride?: string;   // modelKey alterno (vista SP4) cuando difiere del IED
  mapBrandLock?: string;    // marca elegida en el landing (modo "Mapear a SCL"):
                            // habilita el mapeo sin exigir referencia concreta
  selectedIed: string;
  status:      Status;
  error:       string|null;
}

function emptyTab(): TabState {
  return { rows:[], sclNodes:[], selectedIed:"", status:"idle", error:null };
}

interface NbState {
  modelsLoaded: boolean;
  tabs:         Map<string, TabState>;

  // Acceso al estado de la pestaña activa
  getTab:       (id: string) => TabState;
  setTab:       (id: string, patch: Partial<TabState>) => void;
  removeTab:    (id: string) => void;

  initModels:   () => Promise<void>;

  runInference:              (tabId:string, signals:string[], iedRef:string) => Promise<void>;
  runValidation:             (tabId:string, listBytes:Uint8Array, listName:string, sclBytes:Uint8Array, sclName?:string) => Promise<void>;
  runInferenceAndValidation: (tabId:string, signals:string[], iedRef:string, sclBytes:Uint8Array) => Promise<void>;
  validateExistingRows:      (tabId:string, sclBytes:Uint8Array, sclName?:string) => Promise<void>;
  reInferForIed:             (tabId:string, iedRef:string, modelOverride?:string) => Promise<void>;
  addRows:                   (tabId:string, newRows:NbSignal[]) => void;
  clearResults:              (tabId:string) => void;
}

const BASE_URL = import.meta.env.BASE_URL;

// Helper para clonar el Map (Zustand necesita nueva referencia)
function cloneMap(m: Map<string,TabState>): Map<string,TabState> {
  return new Map(m);
}

export const useNbStore = create<NbState>((set, get) => ({
  modelsLoaded: false,
  tabs: new Map<string, TabState>(),

  getTab: (id) => get().tabs.get(id) ?? emptyTab(),

  setTab: (id, patch) => set(s => {
    const m   = cloneMap(s.tabs);
    const cur = m.get(id) ?? emptyTab();
    m.set(id, { ...cur, ...patch });
    return { tabs: m };
  }),

  removeTab: (id) => set(s => {
    const m = cloneMap(s.tabs);
    m.delete(id);
    return { tabs: m };
  }),

  async initModels() {
    if (get().modelsLoaded) return;
    try {
      await loadModels(`${BASE_URL}transformer_models.json`);
      set({ modelsLoaded: true });
    } catch(e) { console.error("Error cargando modelos Transformer:", e); }
  },

  async runInference(tabId, signals, iedRef) {
    get().setTab(tabId, { status:"loading", error:null });
    try {
      const rows = await predictBatch(signals, iedRef);
      get().setTab(tabId, { rows, selectedIed:iedRef, status:"ready", sclNodes:[] });
    } catch(e) { get().setTab(tabId, { status:"error", error:e instanceof Error?e.message:String(e) }); }
  },

  async runValidation(tabId, listBytes, _listName, sclBytes, sclName) {
    get().setTab(tabId, { status:"loading", error:null });
    try {
      const parsed   = await parseSignalList(listBytes, _listName);
      const sclNodes = parseSclNodes(sclBytes);
      const initial: NbSignal[] = parsed.map(p=>({ signal:p.signal, mmsNode:p.mmsNode??"—", iedRef:"" }));
      const rows = await validateInScl(initial, sclNodes);
      get().setTab(tabId, { rows, sclNodes, sclText: new TextDecoder().decode(sclBytes), sclFileName: sclName, status:"ready" });
    } catch(e) { get().setTab(tabId, { status:"error", error:e instanceof Error?e.message:String(e) }); }
  },

  async runInferenceAndValidation(tabId, signals, iedRef, sclBytes) {
    get().setTab(tabId, { status:"loading", error:null });
    try {
      const predicted = await predictBatch(signals, iedRef);
      const sclNodes  = parseSclNodes(sclBytes);
      const rows      = await validateInScl(predicted, sclNodes);
      get().setTab(tabId, { rows, sclNodes, sclText: new TextDecoder().decode(sclBytes), selectedIed:iedRef, status:"ready" });
    } catch(e) { get().setTab(tabId, { status:"error", error:e instanceof Error?e.message:String(e) }); }
  },

  async validateExistingRows(tabId, sclBytes, sclName) {
    const { rows } = get().getTab(tabId);
    // Siempre parsear y guardar los nodos SCL, incluso si rows está vacío.
    // Así el SCL queda disponible cuando el usuario añada señales después.
    get().setTab(tabId, { status:"loading" });
    try {
      const sclNodes = parseSclNodes(sclBytes);
      if (rows.length) {
        const updated = await validateInScl(rows, sclNodes);
        get().setTab(tabId, { rows:updated, sclNodes, sclText: new TextDecoder().decode(sclBytes), sclFileName: sclName, status:"ready" });
      } else {
        // Guardar SCL aunque no haya señales aún
        get().setTab(tabId, { sclNodes, sclText: new TextDecoder().decode(sclBytes), sclFileName: sclName, status:"idle" });
      }
    } catch(e) { get().setTab(tabId, { status:"error", error:e instanceof Error?e.message:String(e) }); }
  },

  async reInferForIed(tabId, iedRef, modelOverride) {
    const { rows, sclNodes } = get().getTab(tabId);
    get().setTab(tabId, { selectedIed:iedRef, modelOverride: modelOverride || undefined });
    // Actualizar el título de la pestaña en pagesStore
    try { usePagesStore.getState().rename(tabId, `Señales — ${iedRef}`); } catch(_) {/**/}
    if (!rows.length) return;
    get().setTab(tabId, { status:"loading" });
    try {
      const predicted = await predictBatch(rows.map(r=>r.signal), iedRef, modelOverride);
      const updated   = sclNodes.length ? await validateInScl(predicted, sclNodes) : predicted;
      get().setTab(tabId, { rows:updated, selectedIed:iedRef, status:"ready" });
    } catch(e) { get().setTab(tabId, { status:"error", error:e instanceof Error?e.message:String(e) }); }
  },

  addRows: (tabId, newRows) => {
    const { rows } = get().getTab(tabId);
    get().setTab(tabId, { rows:[...rows, ...newRows] });
  },
  clearResults: (tabId) => get().setTab(tabId, { rows:[], sclNodes:[], sclText: undefined, sclFileName: undefined, mapSclText: undefined, mapSclFileName: undefined, mapIeds: undefined, mapDatasets: undefined, mapIedTarget: undefined }),
}));
